import { useMemo, useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, FlaskConical, Loader2, Plus } from "lucide-react";
import { useActiveOrg, useEvents } from "@/core/active-event";
import { ensureSession } from "@/core/auth";
import { orgMembersQueryOptions } from "@/core/admin";
import { memberEmails } from "@/core/org-members";
import { OrgShell } from "@/components/org/OrgShell";
import { SectionPage } from "@/components/settings/primitives";
import { EventCard, ScopeChips } from "@/components/org/EventCard";
import {
  eventOrder,
  inScope,
  scopeCounts,
  showState,
  SCOPES,
  type Scope,
} from "@/core/event-scope";
import {
  createEvent,
  orgEventsQueryOptions,
  SANDBOX_PLAN_CODE,
  slugify,
  slugifyLive,
} from "@/core/org-events";
import { requireSession } from "@/lib/auth-guard";

// Tenant home — every event in ONE organization, plus the create/duplicate
// actions. Sits between the org-less root portal ("/" — all your orgs) and the
// event workspace ("/:orgSlug/:eventSlug").
//
// Until this existed, /:orgSlug 404'd and a tenant admin had NO way to create
// an event from the console: the only create form lived on /platform behind the
// sysadmin gate, calling the control-plane action. The org-scoped
// `POST /events/create` has always been open to owner/admin/member.
//
// Events come from GET /orgs/events, scoped to this tenant and membership-gated
// server-side. Deliberately NOT a client filter over the shared
// /me/admin-events store: that store is the cross-org set the root portal and
// the event switcher need, and a page about one tenant shouldn't grow with how
// many other orgs you happen to belong to.
export const Route = createFileRoute("/$orgSlug/")({
  beforeLoad: async ({ location, context }) => {
    const user = await requireSession(location.href);
    context.queryClient.setQueryData(["session", "me"], user);
    return { user };
  },
  component: OrgHome,
});

type FormKind = "event" | "sandbox";

function OrgHome() {
  const params = useParams({ from: "/$orgSlug/" });
  // Org name/logo and the active-org pin both come from OrgShell, which wraps
  // every org-scoped page.
  const [form, setForm] = useState<FormKind | null>(null);
  const [scope, setScope] = useState<Scope>("active");

  const eventsQ = useQuery(orgEventsQueryOptions(params.orgSlug));
  const allEvents = eventsQ.data?.events ?? [];
  // Plan cap (plans.maxEvents) — the demo plan allows exactly one event.
  const canCreateEvents = eventsQ.data?.canCreateEvents ?? true;

  // Counts are over the WHOLE set, so a chip reading "Past 12" is true
  // regardless of which chip is selected.
  const counts = useMemo(() => scopeCounts(allEvents), [allEvents]);

  const orgEvents = useMemo(
    () =>
      allEvents
        .filter((e) => inScope(showState(e).rank, scope))
        .sort(eventOrder),
    [allEvents, scope],
  );

  const stillLoading = eventsQ.isPending;
  const error = eventsQ.error ? (eventsQ.error as Error).message : null;

  return (
    <OrgShell orgSlug={params.orgSlug}>
      {/* Same page primitive as the settings sections — identical width,
          padding, scroll container and header layout, so moving between
          sidebar items doesn't resize the content or reset its scroll.
          `actions` is the primitive's own top-right slot. */}
      <SectionPage
        title="Events"
        description={counts.all === 1 ? "1 event" : `${counts.all} events`}
        actions={
          form || !canCreateEvents ? undefined : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setForm("sandbox")}
                className="inline-flex items-center gap-1.5 rounded-xl border border-plum-200/60 bg-white px-3.5 py-2 text-[13px] font-semibold text-plum-700 shadow-fluent-sm transition hover:text-plum-900 dark:border-white/10 dark:bg-surface-raised dark:text-plum-200/70 dark:hover:text-plum-50"
              >
                <FlaskConical className="h-3.5 w-3.5" /> New sandbox event
              </button>
              <button
                type="button"
                onClick={() => setForm("event")}
                className="inline-flex items-center gap-1.5 rounded-xl bg-plum-700 px-3.5 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800"
              >
                <Plus className="h-3.5 w-3.5" /> New event
              </button>
            </div>
          )
        }
      >
        {/* Always rendered, never gated on load state — the row is part of the
            page's shape, and having it appear after the fetch shifted
            everything below it. Counts read 0 until the data lands. */}
        <ScopeChips scope={scope} onScope={setScope} counts={counts} />

        {form && (
          <NewEventForm
            kind={form}
            orgSlug={params.orgSlug}
            onCancel={() => setForm(null)}
            onDone={() => setForm(null)}
          />
        )}

        {stillLoading ? (
          <ul className="grid gap-2.5 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <li key={i}>
                <div className="flex items-center gap-3 rounded-2xl border border-plum-200/60 bg-white p-3.5 shadow-fluent-sm ring-1 ring-plum-200/20 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
                  <span className="h-11 w-11 shrink-0 animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
                  <span className="min-w-0 flex-1 space-y-1.5">
                    <span className="block h-3.5 w-32 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                    <span className="block h-3 w-20 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-[13px] text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
            Couldn't load events: {error}
          </div>
        ) : orgEvents.length === 0 ? (
          <div className="rounded-2xl border border-plum-200/60 bg-white p-8 text-center shadow-fluent ring-1 ring-plum-200/30 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
              <CalendarDays className="h-5 w-5" />
            </div>
            <p className="text-[14px] font-semibold text-plum-900 dark:text-plum-50">
              {counts.all === 0
                ? "No events yet"
                : `No ${SCOPES.find((s) => s.key === scope)?.label.toLowerCase()} events`}
            </p>
            <p className="mt-1 text-[13px] text-plum-700/55 dark:text-plum-200/45">
              {counts.all === 0 ? (
                "Create one above — or start with a sandbox to try things out without emailing anyone."
              ) : (
                <button
                  type="button"
                  onClick={() => setScope("all")}
                  className="font-medium text-plum-700 underline-offset-2 hover:underline dark:text-plum-200/70"
                >
                  Show all {counts.all}
                </button>
              )}
            </p>
          </div>
        ) : (
          <ul className="grid gap-2.5 sm:grid-cols-2">
            {orgEvents.map((e) => (
              <EventCard key={e.eventId} event={e} orgSlug={params.orgSlug} />
            ))}
          </ul>
        )}
      </SectionPage>
    </OrgShell>
  );
}

function NewEventForm({
  kind,
  orgSlug,
  onCancel,
  onDone,
}: {
  kind: FormKind;
  orgSlug: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const loadEvents = useEvents((s) => s.load);
  const isSandbox = kind === "sandbox";

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [venue, setVenue] = useState("");
  const [tz, setTz] = useState("");

  // Host email is a PICK, never free text. A sandbox event redirects every
  // guest send to this address, so one typo'd domain silently black-holes the
  // whole event's mail (SES accepts, then hard-bounces, and comms_log still
  // reads "sent"). Choosing from the org's own members makes that unreachable.
  const { organizationId } = useActiveOrg();
  const { data: me } = useQuery({
    queryKey: ["session", "me"],
    queryFn: () => ensureSession(),
    staleTime: 30_000,
  });
  // Shared roster query (same key + shape as Members & invites) — this form
  // used to cache a bare array under the same key and crash after visiting
  // that page. memberEmails() also tolerates any stray shape.
  const membersQ = useQuery(orgMembersQueryOptions(organizationId));
  // Own address first, then the rest of the org. Union-with-self so the list is
  // never empty while members are loading or if the BA call fails.
  const hostOptions = useMemo(() => {
    const emails = [
      ...(me?.email ? [me.email] : []),
      ...memberEmails(membersQ.data),
    ];
    return [...new Set(emails)];
  }, [me?.email, membersQ.data]);
  const [hostEmail, setHostEmail] = useState("");
  // Default to yourself — you are the one creating the event.
  const hostValue = hostEmail || (isSandbox ? (hostOptions[0] ?? "") : "");

  // The backend rejects a redirect-plan event with no host address
  // (HOST_EMAIL_REQUIRED) — mirror that here so it's a disabled button, not a
  // round trip.
  const err = !name.trim()
    ? "Name is required"
    : !slug.trim()
      ? "Slug is required"
      : !startsAt || !endsAt
        ? "Start and end are required"
        : endsAt < startsAt
          ? "End must be after start"
          : isSandbox && !hostValue
            ? "Host email is required for a sandbox"
            : null;

  const create = useMutation({
    mutationFn: () =>
      createEvent({
        name: name.trim(),
        slug: slug.trim(),
        startsAt,
        endsAt,
        ...(venue.trim() ? { venueName: venue.trim() } : {}),
        ...(tz.trim() ? { ianaTimezone: tz.trim() } : {}),
        ...(hostValue ? { hostEmail: hostValue } : {}),
        ...(isSandbox ? { planCode: SANDBOX_PLAN_CODE } : {}),
      }),
    onSuccess: async (res) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["org-events", orgSlug] }),
        loadEvents(),
      ]);
      toast.success(`Created ${res.event.name}`);
      onDone();
      void navigate({
        to: "/$orgSlug/$eventSlug",
        params: { orgSlug, eventSlug: res.event.slug },
      });
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Failed to create event"),
  });

  const inputCls =
    "w-full rounded-xl border border-plum-200/70 bg-white px-3 py-2 text-[13px] text-plum-900 outline-none transition focus:border-plum-400 dark:border-white/10 dark:bg-surface dark:text-plum-50";

  return (
    <div className="rounded-2xl border border-plum-200/60 bg-white p-5 shadow-fluent ring-1 ring-plum-200/25 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          {isSandbox && (
            <FlaskConical className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          )}
          {isSandbox ? "New sandbox event" : "New event"}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12.5px] font-medium text-plum-700/55 transition hover:text-plum-900 dark:text-plum-200/45 dark:hover:text-plum-50"
        >
          Cancel
        </button>
      </div>

      {isSandbox && (
        <p className="mb-4 rounded-xl bg-amber-50 px-3 py-2 text-[12.5px] leading-relaxed text-amber-900 dark:bg-amber-400/10 dark:text-amber-200">
          A sandbox event is free and its guest email is contained: invites,
          reminders and sign-in codes all go to the host address below instead
          of to guests, each one labelled with who it would have reached.
        </p>
      )}

      <div className="space-y-4">
        <Field label="Name">
          <input
            className={inputCls}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
            placeholder={
              isSandbox ? "Acme Summit (sandbox)" : "Acme Summit 2027"
            }
          />
        </Field>
        <Field label="Slug" hint="Used in the event URL">
          <input
            className={inputCls}
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugifyLive(e.target.value));
            }}
            placeholder="acme-summit-2027"
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Starts">
            <input
              className={inputCls}
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </Field>
          <Field label="Ends">
            <input
              className={inputCls}
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </Field>
        </div>
        <Field
          label="Host email"
          hint={
            isSandbox
              ? "required — every guest email lands here"
              : "optional — org members only"
          }
        >
          <select
            className={inputCls}
            value={hostValue}
            onChange={(e) => setHostEmail(e.target.value)}
          >
            {!isSandbox && <option value="">No host address</option>}
            {hostOptions.map((email) => (
              <option key={email} value={email}>
                {email}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Venue" hint="optional">
            <input
              className={inputCls}
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="The Grand Hotel"
            />
          </Field>
          <Field label="Timezone" hint="optional, IANA">
            <input
              className={inputCls}
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              placeholder="America/New_York"
            />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-3 pt-1">
          {err && (
            <span className="text-[12px] text-plum-700/55 dark:text-plum-200/45">
              {err}
            </span>
          )}
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={!!err || create.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-plum-700 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSandbox ? "Create sandbox" : "Create event"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline gap-1.5">
        <span className="text-[12.5px] font-semibold text-plum-800 dark:text-plum-100">
          {label}
        </span>
        {hint && (
          <span className="text-[11.5px] text-plum-700/45 dark:text-plum-200/35">
            {hint}
          </span>
        )}
      </span>
      {children}
    </label>
  );
}
